import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import CharacterCard from '@/components/CharacterCard';
import { Check, Eye, EyeOff, BookOpen, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('stats'); // 'stats' | 'ignored'
  const [ignored, setIgnored] = useState([]);
  const [ignoredLoading, setIgnoredLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    api.get('/flashcards/stats')
      .then(({ data }) => setStats(data))
      .catch(() => toast.error('Could not load stats'))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const loadIgnored = useCallback(async () => {
    setIgnoredLoading(true);
    try {
      const { data } = await api.get('/flashcards/ignored');
      setIgnored(data.items || []);
      setView('ignored');
    } catch {
      toast.error('Could not load ignored list');
    } finally {
      setIgnoredLoading(false);
    }
  }, []);

  const unignore = async (id) => {
    try {
      await api.delete(`/flashcards/${id}`);
      setIgnored((s) => s.filter((c) => c.id !== id));
      toast.success('Removed from Ignored');
      // Refresh stats
      const { data } = await api.get('/flashcards/stats');
      setStats(data);
    } catch {
      toast.error('Could not update');
    }
  };

  if (loading || authLoading || !user) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>;
  }

  if (view === 'ignored') {
    return (
      <div className="container-page py-12 lg:py-16" data-testid="profile-ignored-view">
        <button onClick={() => setView('stats')} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#1E3A8A] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to profile
        </button>
        <div className="mb-8">
          <h1 className="font-serif-display text-4xl text-slate-900 mb-1">Ignored people</h1>
          <p className="text-sm text-slate-500">{ignored.length} marked as ignored. Click to view, or remove from this list to bring them back into your flashcard deck.</p>
        </div>
        {ignoredLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
        ) : ignored.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl">
            <p className="font-serif-display text-2xl text-slate-700 mb-2">Nothing ignored</p>
            <p className="text-sm text-slate-500">People you mark as "Ignore" in the flashcard deck will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ignored.map((c) => (
              <div key={c.id} className="relative">
                <CharacterCard character={c} />
                <button
                  onClick={(e) => { e.preventDefault(); unignore(c.id); }}
                  className="absolute top-3 right-3 z-10 text-xs bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 px-2.5 py-1 rounded-md font-medium transition-colors"
                  title="Remove from ignored"
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const cards = [
    { key: 'known', label: 'Known', value: stats?.known || 0, Icon: Check, color: 'emerald', desc: 'People you\'ve mastered.' },
    { key: 'want_to_know', label: 'Want to Know', value: stats?.want_to_know || 0, Icon: Eye, color: 'amber', desc: 'On your study list.' },
    { key: 'remaining', label: 'Remaining', value: stats?.remaining || 0, Icon: BookOpen, color: 'blue', desc: 'Not yet reviewed.' },
    { key: 'ignore', label: 'Ignored', value: stats?.ignore || 0, Icon: EyeOff, color: 'rose', desc: 'Skipped from your deck.' },
  ];

  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-[#DBEAFE] text-[#1E3A8A] border-[#BFDBFE]',
    rose: 'bg-rose-50 text-rose-600 border-rose-200',
  };

  return (
    <div className="container-page py-12 lg:py-16" data-testid="profile-page">
      <div className="mb-10 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] text-2xl font-serif-display">
          {(user.name || user.email)[0].toUpperCase()}
        </div>
        <div>
          <h1 className="font-serif-display text-4xl text-slate-900">{user.name || user.email}</h1>
          <p className="text-sm text-slate-500 font-mono">{user.email}{user.is_admin && <span className="ml-2 text-[#0F766E] font-sans not-italic">· Admin</span>}</p>
        </div>
      </div>

      <h2 className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-4">Your Study Stats</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map(({ key, label, value, Icon, color, desc }) => (
          <div key={key} className={`border rounded-xl p-5 ${colorMap[color]}`}>
            <div className="flex items-center justify-between mb-3">
              <Icon className="w-5 h-5" />
              <span className="text-3xl font-serif-display">{value}</span>
            </div>
            <div className="text-sm font-semibold">{label}</div>
            <div className="text-xs opacity-80 mt-0.5">{desc}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-serif-display text-2xl text-slate-900">Review ignored people</h3>
            <p className="text-sm text-slate-500 mt-1">Take another look at the {stats?.ignore || 0} people you've previously skipped.</p>
          </div>
          <button
            onClick={loadIgnored}
            data-testid="view-ignored-button"
            disabled={ignoredLoading}
            className="btn-primary text-sm"
          >
            {ignoredLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />} View ignored
          </button>
        </div>
      </div>

      <div className="mt-8 text-sm text-slate-500">
        <Link to="/flashcards" className="text-[#1E3A8A] hover:underline">→ Back to flashcards</Link>
      </div>
    </div>
  );
}
